import { useState, useEffect, useCallback, useRef } from 'react';

export function useScrollSpy(
  categories,
  { offset = 180, getCategoryId, getSections, getSectionId, isCategorySelectable },
) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const subNavRef = useRef(null);

  // Set first category on initial load. When the caller supplies
  // isCategorySelectable we land on the first category it accepts (e.g. the
  // first higher category that is actually open right now) instead of always
  // category[0], which may be closed today. Falls back to the first category
  // so a fully-closed menu still renders something.
  useEffect(() => {
    if (categories.length && !isInitialized) {
      const firstCategory =
        (isCategorySelectable ? categories.find((cat) => isCategorySelectable(cat)) : null) ||
        categories[0];
      const sections = getSections?.(firstCategory) || [];
      setActiveCategory(getCategoryId(firstCategory));
      if (sections.length) setActiveSubCategory(getSectionId(sections[0]));
      setIsInitialized(true);
    }
  }, [
    categories,
    isInitialized,
    getCategoryId,
    getSections,
    getSectionId,
    isCategorySelectable,
  ]);

  const handleScroll = useCallback(() => {
    if (!categories.length || !isInitialized) return;

    // Only check scrolledToBottom if content has rendered
    const hasContent = document.body.scrollHeight > window.innerHeight;
    const scrolledToBottom =
      hasContent && window.innerHeight + window.scrollY >= document.body.scrollHeight - 10;

    if (scrolledToBottom) {
      const lastCategory = categories[categories.length - 1];
      const sections = getSections?.(lastCategory) || [];

      // Skip if last category has no sections
      if (sections.length === 0) return;

      const lastSection = sections[sections.length - 1];
      setActiveCategory(getCategoryId(lastCategory));
      if (lastSection) setActiveSubCategory(getSectionId(lastSection));
      return;
    }

    let currentSection = null;
    let currentCategory = null;

    for (const category of categories) {
      const sections = getSections?.(category) || [];

      for (const section of sections) {
        const sectionId = getSectionId(section);
        const element = document.getElementById(sectionId);
        if (!element) continue;

        const { top } = element.getBoundingClientRect();
        if (top <= offset) {
          currentSection = sectionId;
          currentCategory = getCategoryId(category);
        }
      }
    }

    if (currentSection && currentCategory) {
      setActiveSubCategory(currentSection);
      setActiveCategory(currentCategory);
    }
  }, [categories, offset, isInitialized, getCategoryId, getSections, getSectionId]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!subNavRef.current) return;
    const activeButton = subNavRef.current.querySelector('[data-active="true"]');
    if (activeButton) {
      activeButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeSubCategory]);

  const scrollTo = useCallback((id, scrollOffset = 180) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - scrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return {
    activeCategory,
    activeSubCategory,
    scrollTo,
    subNavRef,
    setActiveCategory,
    setActiveSubCategory,
  };
}
